import React, { useEffect, useState } from 'react';
import shuffle from 'lodash.shuffle';
import { useTranslation } from 'react-i18next';

const WhichCameFirst = ({ events, onQuit }) => {
  const { t } = useTranslation();
  const [eventPairs, setEventPairs] = useState([]);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);

  useEffect(() => {
    if (!events.length) return;
    const shuffled = shuffle(events.filter(e => e.title && e.year)).slice(0, 12);
    const pairs = [];
    for (let i = 0; i < shuffled.length - 1; i += 2) {
      pairs.push([shuffled[i], shuffled[i + 1]]);
    }
    setEventPairs(pairs);
  }, [events]);

  useEffect(() => {
    if (selected !== null) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [selected, currentPairIndex]);

  const handleTimeout = () => {
    setSelected(-1);
    setResult(false);
    setStreak(0);
  };

  const handleChoice = (choiceIndex) => {
    if (selected !== null) return;
    const [eventA, eventB] = eventPairs[currentPairIndex];
    const correctIndex = eventA.year <= eventB.year ? 0 : 1;

    setSelected(choiceIndex);
    if (choiceIndex === correctIndex) {
      setResult(true);
      setScore(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      setResult(false);
      setStreak(0);
    }
  };

  const handleNext = () => {
    if (currentPairIndex + 1 < eventPairs.length) {
      setCurrentPairIndex(prev => prev + 1);
      setSelected(null);
      setResult(null);
      setTimeLeft(15);
    } else {
      setGameOver(true);
    }
  };

  const handleQuit = () => {
    if (window.confirm(t("quiz.which.quitConfirm"))) {
      onQuit();
    }
  };

  if (eventPairs.length === 0) {
    return <div className="text-center text-gray-400 mt-10">{t("quiz.which.loading")}</div>;
  }

  if (gameOver) {
    return (
      <div className="max-w-xl mx-auto p-6 bg-gray-900 text-white rounded-lg shadow-xl mt-10 text-center">
        <h2 className="text-3xl font-bold text-purple-400 mb-4">{t("quiz.which.gameOver")}</h2>
        <p className="text-lg text-green-400 mb-2">
          {t("quiz.which.finalScore")}: {score} / {eventPairs.length}
        </p>
        <p className="text-md text-yellow-400 mb-6">
          🏆 {t("quiz.which.bestStreak")}: {bestStreak}
        </p>
        <button
          onClick={onQuit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
        >
          🔙 {t("quiz.which.returnToMenu")}
        </button>
      </div>
    );
  }

  const [eventA, eventB] = eventPairs[currentPairIndex];
  const correctIndex = eventA.year <= eventB.year ? 0 : 1;

  return (
    <div className="relative max-w-2xl mx-auto p-6 bg-gray-900 text-white rounded-lg shadow-xl mt-10">
      <button
        onClick={handleQuit}
        className="absolute top-4 right-4 text-red-400 hover:text-red-500 text-sm font-bold"
      >
        ✖️ {t("quiz.which.quit")}
      </button>

      <div className="flex justify-center mb-6">
        <h2 className="text-2xl font-bold text-purple-400">
          🕑 {t("quiz.which.title")} ({currentPairIndex + 1}/{eventPairs.length})
        </h2>
      </div>

      {selected === null && (
        <div className="text-center text-red-400 font-bold mb-4">
          ⏳ {timeLeft} {t("quiz.which.secondsLeft")}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 justify-center">
        {[eventA, eventB].map((event, idx) => (
          <button
            key={idx}
            onClick={() => handleChoice(idx)}
            className={`flex-1 bg-gray-800 border-2 p-4 rounded-lg transition text-center ${
              selected !== null
                ? idx === correctIndex
                  ? 'border-green-500'
                  : idx === selected
                  ? 'border-red-500'
                  : 'border-gray-600'
                : 'border-gray-600 hover:border-purple-500'
            }`}
            disabled={selected !== null}
          >
            <h3 className="text-xl font-bold text-blue-300 mb-2">{event.title}</h3>
            {selected !== null && (
              <p className="text-gray-400 text-sm">📅 {event.year}</p>
            )}
          </button>
        ))}
      </div>

      {selected !== null && (
        <div className="mt-6 text-center space-y-4">
          {result ? (
            <p className="text-green-400 font-bold text-lg">{t("quiz.which.correct")}</p>
          ) : selected === -1 ? (
            <p className="text-red-400 font-bold text-lg">{t("quiz.which.timeout")}</p>
          ) : (
            <p className="text-red-400 font-bold text-lg">
              ❌ {t("quiz.which.incorrect", {
                title: eventA.year <= eventB.year ? eventA.title : eventB.title
              })}
            </p>
          )}
          <button
            onClick={handleNext}
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-lg font-semibold"
          >
            ➡️ {t("quiz.which.next")}
          </button>
        </div>
      )}

      <div className="mt-6 text-center text-gray-400 text-sm">
        {t("quiz.which.score")}: {score} / {eventPairs.length} <br />
        🔥 {t("quiz.which.streak")}: {streak}
      </div>
    </div>
  );
};

export default WhichCameFirst;